'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { trackEvent } from 'components/shared/UserAnalytics/funnelEvents';
import { ZapIcon } from 'components/ring-air/ZapIcon';
import { AE_DELIVERY_GTM_EVENTS, AE_EMIRATE_STORAGE_KEY } from './config';
import { AE_EMIRATES, aeEmirateGroupOf, parseAeEmirate } from './emirates';
import {
  resolveAeDeliveryWindow,
  type AeDeliveryWindowState,
} from './deliveryWindow';
import { useAeDeliveryEta } from './useAeDeliveryEta';
import { aeDeliveryEtaCopy, aeEmirateLabel } from './copy';
import styles from './index.module.css';

// The buy page remounts its provider tree once ~150-190ms after hydration
// (pre-existing page bug), which would fire SHOWN twice per page view.
// Module-scope timestamp dedupe: mounts closer together than this window
// count as one impression; a genuine client-side nav back to the page
// (always later than this) still re-fires.
const SHOWN_DEDUPE_MS = 2_000;
let shownLastFiredAt = 0;

/**
 * UAE delivery-promise card for /ae/ring/buy. Visually mirrors India's
 * DeliveryEtaBadge card (same grey chrome, zap mark, title typography) but
 * instead of a pincode input it carries a native emirate <select>: the copy
 * is the ops window rule for the chosen emirate's group, in the India-badge
 * sequence (copy first, input below). The express promise applies to the
 * Ring AIR sizing kit only: the card is always mounted on AE, but while a
 * numeric size is selected (isSizingKit=false) it sits in the default state
 * — default copy, placeholder select, select disabled — alongside the
 * backend "Dispatched by" estimate, never a window promise. Before a
 * selection the copy is the time-aware default: "Same day delivery in
 * select Emirates." until 5 PM GST, the next-day variant from 5 PM to
 * midnight (resolved via the null -> dubai best-case bounds). Resolver
 * failure (no Asia/Dubai Intl data) renders the same-day-worded hard
 * default — never a positive time promise and never a cached state. After a
 * selection it is that emirate's group rule, with a flat next-day promise
 * for emirates without same-day / 4-hour service (Ajman, RAK, Fujairah,
 * UAQ) — never "not available" copy. The
 * "Applicable for the sizing kit only" meta line is ALWAYS present, styled
 * like India's "Applicable in select cities." line (the kit's ring shipment
 * follows separately and is not covered by the promise). The bottom bars
 * keep their own qualifier gated to the positive promise states via
 * aeDeliveryEtaShowsSizingKitQualifier. The zap icon is the exact same component
 * (size + lottie intro + reduced-motion handling) as India's
 * DeliveryEtaBadge, passed a constant playToken (only the one-time intro
 * plays), and is hidden on the error copy.
 */
export const AeDeliveryEtaCard = ({
  isSizingKit,
}: {
  /** False while a numeric ring size is selected: the card stays visible but
   * inert — default copy, placeholder select, select disabled. The context
   * already cleared the emirate when the numeric size was picked. Required
   * (no permissive default): a caller that forgot it would otherwise show a
   * live express promise on a numeric-size order. */
  isSizingKit: boolean;
}) => {
  const t = useTranslations('ring');
  // The hook nulls the emirate while a numeric size is selected, so group
  // and windowState resolve from the null default too — a stored emirate
  // restored by a ?size=N deep link can never leak its group's window into
  // the inert default line. Enabled: true, the caller's AE gate passed.
  const {
    selectedEmirate: effectiveEmirate,
    setSelectedEmirate,
    group,
    windowState,
    hasError,
  } = useAeDeliveryEta(true, isSizingKit);

  useEffect(() => {
    // Fire once per page view, not once per window/emirate change — a
    // visitor who keeps the tab open across a window boundary shouldn't
    // re-fire the "shown" event, and the provider-remount double mount is
    // deduped by the module-scope timestamp above. The emirate is read
    // straight from sessionStorage: this child effect runs before the
    // provider's restore effect, so the context value is still null here.
    if (Date.now() - shownLastFiredAt < SHOWN_DEDUPE_MS) return;
    shownLastFiredAt = Date.now();
    let emirate: string | null = null;
    try {
      emirate = parseAeEmirate(sessionStorage.getItem(AE_EMIRATE_STORAGE_KEY));
    } catch {
      // private-mode sessionStorage access can throw — report null.
    }
    trackEvent(AE_DELIVERY_GTM_EVENTS.SHOWN, {
      deliveryWindow: windowState,
      emirate,
    });
  }, []);

  const handleEmirateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = parseAeEmirate(event.target.value || null);
    // Resolve the window for the NEW emirate's group — `windowState` from the
    // hook still reflects the previous selection during this handler, and the
    // two groups disagree between 13:00 and 17:00 GST.
    const nextGroup = aeEmirateGroupOf(next);
    let nextWindow: AeDeliveryWindowState;
    try {
      nextWindow = resolveAeDeliveryWindow(nextGroup);
    } catch {
      nextWindow = 'error';
    }
    trackEvent(AE_DELIVERY_GTM_EVENTS.EMIRATE_SELECTED, {
      // The select is disabled on the inert card, so this handler only runs
      // while the sizing kit is active — effectiveEmirate IS the previous
      // user selection here.
      emirate: next,
      previous: effectiveEmirate,
      deliveryWindow: nextWindow,
      group: nextGroup,
    });
    setSelectedEmirate(next);
  };

  // Sequence mirrors India's DeliveryEtaBadge: title copy first, then the
  // input (here the emirate select). The window line is ALWAYS present:
  // before any selection it is the time-aware default (same-day wording
  // until 5 PM GST, next-day after; error keeps the same-day hard default);
  // after a selection it is that emirate's group rule — ineligible emirates
  // get the flat next-day promise, never "not available". The hook already
  // nulled emirate/group/window together for the inert card, so the three
  // arguments can never disagree.
  const titleContent = aeDeliveryEtaCopy(
    effectiveEmirate,
    group,
    windowState,
    t,
  );
  const showZap = !hasError;

  return (
    <div className={styles.badge}>
      {/* aria-live wraps the WHOLE card so a title swap is announced together
          with the sizing-kit scope note below the selector — announcing the
          title alone would read an unqualified delivery claim to screen
          readers at the exact moment it stops applying. */}
      <div className={styles.card} aria-live="polite">
        <div className={styles.titleRow}>
          {showZap ? <ZapIcon playToken={0} /> : null}
          <p className={styles.title}>
            <strong>{titleContent}</strong>
          </p>
        </div>
        <div className={styles.selectWrapper}>
          <select
            className={`${styles.select}${
              effectiveEmirate ? '' : ` ${styles.selectEmpty}`
            }`}
            value={effectiveEmirate ?? ''}
            onChange={handleEmirateChange}
            disabled={!isSizingKit}
            aria-label={t('ring.aeDelivery.selectEmiratePlaceholder')}
            data-testid="ae-emirate-select"
            data-buttontype="ae-emirate-select"
          >
            <option value="">
              {t('ring.aeDelivery.selectEmiratePlaceholder')}
            </option>
            {AE_EMIRATES.map((emirate) => (
              <option key={emirate} value={emirate}>
                {aeEmirateLabel(emirate, t)}
              </option>
            ))}
          </select>
        </div>
        {/* Permanent and BELOW the selector, like India's "Applicable in
            select cities." meta line — the scope note holds in every state. */}
        <p className={styles.metaLine} data-testid="ae-sizing-kit-note">
          {t('ring.aeDelivery.sizingKitOnlyDisclaimer')}
        </p>
      </div>
    </div>
  );
};
